import { useState, useRef, useEffect } from 'react';
import {
  initialExtraToppingRow,
  RegularMenuCategory as RegularMenuCategoryFallback,
  ComboMenuCategory as ComboMenuCategoryFallback,
  fieldLabelMap,
} from './constants';
import TextTransformDatabaseEnum from '../../../utils/textTransformDatabaseEnum';
import { API_NETWORK_ERROR } from '../../../utils/constants';
import {
  mapRegionalProductUpdateErrorsToForm,
  omitRegionalForbiddenProductKeys,
} from '../../../context-api/api-services/Products.service';
import { useKitchenProductsData } from '../../../context-api/kitchen-products/KitchenProductsDataContext';

const isValidDescription = ({ text, name, setInputValidationError }) => {
  setInputValidationError(false);
  const pattern = /^[a-zA-Z0-9\s.,!?()-]*$/;
  if (name === 'recipewith' && !text.trim()) return '';
  if (!text.trim()) return 'This field is required';
  if (name === 'productname' && text.length > 50) {
    setInputValidationError(true);
    return 'Max 50 characters allowed';
  }
  if (name === 'recipewith' && text.length > 25) {
    setInputValidationError(true);
    return 'Max 25 characters allowed';
  }
  if (name === 'quantity' && text.length > 5) {
    setInputValidationError(true);
    return 'Max 5 characters allowed';
  }
  if (name === 'description' && text.length > 500) {
    setInputValidationError(true);
    return 'Max 500 characters allowed';
  }
  if (!pattern.test(text)) {
    setInputValidationError(true);
    return 'Only letters, numbers, and basic punctuation allowed';
  }
  return '';
};

const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

const initOffers = (productPromotion) => {
  const promo = productPromotion || {};
  const hasActive = Object.values(promo).some((v) => v === true);
  return {
    buy_one_get_one_free: promo.buy_one_get_one_free || false,
    buy_two_get_one_free: promo.buy_two_get_one_free || false,
    buy_one_get_half_price: promo.buy_one_get_half_price || false,
    none: !hasActive,
  };
};

const initServingDays = (days) => {
  if (!days || days.length === 0) return [];
  return days.map(capitalizeFirst);
};

const initPrice = (price) => {
  if (price == null || price === '') return '0.00';
  return Number(price).toFixed(2);
};

const formatPrice = (raw = '') => {
  const digitsOnly = String(raw).replace(/\D/g, '').padStart(3, '0');
  const intPart = digitsOnly.slice(0, -2);
  const decimalPart = digitsOnly.slice(-2);
  return parseFloat(`${parseInt(intPart, 10)}.${decimalPart}`);
};

/** Combo list API uses snake_case; Classification Select uses spaced labels (inverse of TextTransformDatabaseEnum). */
const apiComboCategoryToSelectValue = (raw) => {
  if (raw == null || raw === '' || raw === 'select') return 'select';
  const v = String(raw).trim();
  const enumToLabel = {
    main_dish: 'main dish',
    power_side: 'power side',
    drink_dessert_treat: 'drink or dessert or treat',
    main_course: 'main course',
  };
  return enumToLabel[v] || v;
};

/** API often sends lowercase meal_type; MealTypeList uses title case. */
const normalizeMealTypeForSelect = (raw) => {
  if (raw == null || String(raw).trim() === '') return 'All Day';
  const s = String(raw).trim().toLowerCase();
  const map = {
    'all day': 'All Day',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    brunch: 'Brunch',
  };
  return map[s] || String(raw).trim();
};

const normalizeServingTemperatureForSelect = (raw) => {
  if (raw == null || String(raw).trim() === '') return 'select';
  return String(raw).trim().toLowerCase();
};

const initialProductCategoryFromApi = (product) => {
  const raw = product?.product_category;
  if (raw == null || raw === '' || raw === 'select') return 'select';
  const pt = product?.product_type || 'non_meal_item';
  if (pt === 'meal_item') {
    return apiComboCategoryToSelectValue(raw);
  }
  const needle = String(raw).trim().toLowerCase();
  const found = RegularMenuCategoryFallback.find(
    (opt) => opt !== 'select' && String(opt).toLowerCase() === needle
  );
  return found || String(raw).trim();
};

export const useEditProductForm = ({ product, onUpdate, onClose }) => {
  const { uploadImageCloudinary, regionalUpdateProduct } = useKitchenProductsData();
  const formTopRef = useRef(null);

  const hasIngredientImageFromProduct = Boolean(
    product?.ingredients_image && String(product.ingredients_image).trim() !== ''
  );

  const KitchenName = product?.kitchen_name;
  const categoryForForm = initialProductCategoryFromApi(product);

  const origRef = useRef({
    product_name: product?.product_name || '',
    recipe_with: product?.recipe_with || '',
    product_description: product?.product_description || '',
    product_category: categoryForForm,
    special_marking: product?.special_marking?.[0] || 'select',
    serving_temperature: normalizeServingTemperatureForSelect(product?.serving_temperature),
    cuisine: product?.cuisine?.toLowerCase() || 'select',
    size: product?.size || 'full',
    serving_size: product?.serving_size || 'one_person',
    meal_type: normalizeMealTypeForSelect(product?.meal_type),
    price: initPrice(product?.price ?? product?.selling_price),
    available_quantity:
      product?.available_quantity != null ? String(product.available_quantity) : '0',
    serving_days: initServingDays(product?.serving_days),
    ingredients: product?.ingredients?.length ? [...product.ingredients] : [],
    allergic_ingredients: product?.allergic_ingredients?.length
      ? [...product.allergic_ingredients]
      : [],
    extra_toppings: product?.extra_toppings?.length ? [...product.extra_toppings] : [],
    product_promotion: product?.product_promotion || {},
  });

  const [productType] = useState(product?.product_type || 'non_meal_item');
  const [productName, setProductName] = useState(product?.product_name || '');
  const [recipeWith, setRecipeWith] = useState(product?.recipe_with || '');
  const [description, setDescription] = useState(product?.product_description || '');
  const [productCategory, setProductCategory] = useState(categoryForForm);
  const [specialMarking, setSpecialMarking] = useState(product?.special_marking?.[0] || 'select');
  const [servingTemperature, setServingTemperature] = useState(
    normalizeServingTemperatureForSelect(product?.serving_temperature)
  );
  const [cuisineType, setCuisineType] = useState(product?.cuisine?.toLowerCase() || 'select');
  const [size, setSize] = useState(product?.size || 'full');
  const [servingSize, setServingSize] = useState(product?.serving_size || 'one_person');
  const [mealType, setMealType] = useState(normalizeMealTypeForSelect(product?.meal_type));
  const [price, setPrice] = useState(initPrice(product?.price ?? product?.selling_price));
  const [quantity, setQuantity] = useState(
    product?.available_quantity != null ? String(product.available_quantity) : '0'
  );
  const [selectedDays, setSelectedDays] = useState(initServingDays(product?.serving_days));
  const [offers] = useState(initOffers(product?.product_promotion));
  const [ingredients] = useState(
    product?.ingredients?.length ? product.ingredients : ['']
  );
  const [allergicIngredients] = useState(
    product?.allergic_ingredients?.length ? product.allergic_ingredients : ['']
  );
  const [extraToppings, setExtraToppings] = useState(
    product?.extra_toppings?.length
      ? product.extra_toppings.map((t) => ({ ...t, errors: {} }))
      : [{ ...initialExtraToppingRow }]
  );
  const [imageFile, setImageFile] = useState('');
  const [previewUrl, setPreviewUrl] = useState(product?.image_url || '');
  const [ingredientImageFile, setIngredientImageFile] = useState('');
  const [ingredientPreviewUrl, setIngredientPreviewUrl] = useState(
    product?.ingredients_image || ''
  );

  const productImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });
  const ingredientImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });

  const [errors, setErrors] = useState({
    productname: '',
    recipewith: '',
    description: '',
    quantity: '',
  });
  const [inputValidationError, setInputValidationError] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editConfirmed, setEditConfirmed] = useState(false);
  const [editingFields, setEditingFields] = useState({});
  const [changedFields, setChangedFields] = useState({});

  useEffect(() => {
    console.log('api hit', 'getProductCategory');
  }, []);

  const categoryList =
    productType === 'non_meal_item' ? RegularMenuCategoryFallback : ComboMenuCategoryFallback;

  const getFileCacheKey = (file) =>
    file && file.name != null ? `${file.name}-${file.size}-${file.lastModified ?? 0}` : '';

  const startEdit = (key) => setEditingFields((prev) => ({ ...prev, [key]: true }));

  const cancelField = (key, setter) => {
    setter(origRef.current[key]);
    setEditingFields((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setChangedFields((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  };

  const trackField = (apiKey, newVal) => {
    const origVal = origRef.current[apiKey];
    setChangedFields((prev) => {
      const next = { ...prev };
      if (String(newVal ?? '') !== String(origVal ?? '')) {
        next[apiKey] = newVal;
      } else {
        delete next[apiKey];
      }
      return next;
    });
  };

  const onChangeHandler = (key) => (e) => {
    setInputValidationError(false);
    const val = e.target.value;
    const newErrors = { ...errors };
    if (key === 'productname') {
      setProductName(val);
      trackField('product_name', val);
      newErrors.productname = isValidDescription({
        text: val,
        name: 'productname',
        setInputValidationError,
      });
    }
    if (key === 'recipewith') {
      setRecipeWith(val);
      trackField('recipe_with', val);
      newErrors.recipewith = isValidDescription({
        text: val,
        name: 'recipewith',
        setInputValidationError,
      });
    }
    if (key === 'description') {
      setDescription(val);
      trackField('product_description', val);
      newErrors.description = isValidDescription({
        text: val,
        name: 'description',
        setInputValidationError,
      });
    }
    if (key === 'quantity') {
      setQuantity(val);
      trackField('available_quantity', val);
      newErrors.quantity = isValidDescription({
        text: val,
        name: 'quantity',
        setInputValidationError,
      });
    }
    setErrors(newErrors);
  };

  const cleanedToppings = extraToppings.filter(
    (t) => t.topping_name.trim() !== '' && Number(t.topping_price) !== 0
  );
  const cleanExtraToppings = cleanedToppings.map(
    ({ errors: _e, topping_price, topping_available_quantity, ...rest }) => ({
      ...rest,
      topping_price: formatPrice(topping_price),
      topping_available_quantity: parseInt(topping_available_quantity, 10),
    })
  );

  const hasServingDaysChanged = () => {
    const orig = [...origRef.current.serving_days].sort();
    const curr = [...selectedDays].sort();
    return JSON.stringify(orig) !== JSON.stringify(curr);
  };

  const hasToppingsChanged = () => {
    const origRaw = origRef.current.extra_toppings || [];
    const origClean = origRaw.map(
      ({ errors: _e, topping_price, topping_available_quantity, ...rest }) => ({
        ...rest,
        topping_price: parseFloat(topping_price) || 0,
        topping_available_quantity: parseInt(topping_available_quantity, 10) || 0,
      })
    );
    return JSON.stringify(origClean) !== JSON.stringify(cleanExtraToppings);
  };

  const handleUpdateProduct = async () => {
    setMissingFields([]);
    const errorMsg = [];

    if (inputValidationError) {
      errorMsg.push({
        errorMessage: 'Please check your input — a required field may be missing or invalid.',
        param: 'input_box',
      });
    }
    if (!productName.trim()) {
      errorMsg.push({ errorMessage: 'Product name is required', param: 'product_name' });
    }
    if (selectedDays.length === 0) {
      errorMsg.push({ errorMessage: 'Serving days required', param: 'serving_days' });
    }
    if (servingTemperature === 'select') {
      errorMsg.push({ errorMessage: 'Serving temperature required', param: 'serving_temperature' });
    }
    if (specialMarking === 'select') {
      errorMsg.push({ errorMessage: 'Special marking required', param: 'special_marking' });
    }
    if (productCategory === 'select') {
      errorMsg.push({ errorMessage: 'Product category is required', param: 'product_category' });
    }
    if (!cuisineType || cuisineType === 'select') {
      errorMsg.push({ errorMessage: 'Cuisine is required', param: 'cuisine' });
    }

    // Mirror create-form validation for description (recipe_with is optional on edit)
    if (!description || description.trim() === '') {
      errorMsg.push({
        errorMessage: 'Product description is required',
        param: 'product_description',
      });
    }

    // if (recipeWith == null || String(recipeWith ?? '').trim() === '') {
    //   errorMsg.push({ errorMessage: 'Recipe with is required', param: 'recipe_with' });
    // }

    // Mirror extra toppings validation from create-form for non_meal_item
    if (productType === 'non_meal_item' && extraToppings?.length) {
      extraToppings.forEach((t, index) => {
        const hasName = t.topping_name?.trim();
        if (!hasName) return;

        const missing = [];
        const priceVal = t.topping_price;
        const qtyVal = t.topping_available_quantity;

        const hasPrice =
          priceVal !== undefined &&
          priceVal !== null &&
          String(priceVal).trim() !== '' &&
          Number(parseFloat(String(priceVal).replace(/\D/g, ''))) > 0;

        const hasQty =
          qtyVal !== undefined &&
          qtyVal !== null &&
          String(qtyVal).trim() !== '' &&
          !Number.isNaN(Number(qtyVal)) &&
          Number(qtyVal) >= 0;

        if (!hasPrice) missing.push('price');
        if (!hasQty) missing.push('quantity');

        if (missing.length) {
          errorMsg.push({
            errorMessage: `Extra topping "${hasName}" (row ${index + 1}): please add ${missing.join(' and ')}.`,
            param: 'extra_topping',
          });
        }
      });
    }
    if (errorMsg.length > 0) {
      setMissingFields(errorMsg);
      setTimeout(() => {
        if (formTopRef.current) {
          formTopRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }, 100);
      return;
    }

    const scrollTopError = () => {
      setTimeout(() => {
        if (formTopRef.current) {
          formTopRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }, 100);
    };

    if (!KitchenName || String(KitchenName).trim() === '') {
      setMissingFields([
        { errorMessage: 'Kitchen name is missing for this product.', param: 'kitchen_name' },
      ]);
      scrollTopError();
      return;
    }
    const sku = product?.product_sku != null ? String(product.product_sku).trim() : '';
    if (!sku) {
      setMissingFields([
        { errorMessage: 'Product SKU is missing for this product.', param: 'product_sku' },
      ]);
      scrollTopError();
      return;
    }

    const hasAnyFieldUpdate =
      Object.keys(changedFields).length > 0 ||
      hasServingDaysChanged() ||
      (productType === 'non_meal_item' && hasToppingsChanged()) ||
      Boolean(imageFile) ||
      (hasIngredientImageFromProduct && Boolean(ingredientImageFile));

    if (!hasAnyFieldUpdate) {
      setMissingFields([{ errorMessage: 'No changes to save.', param: 'update_product' }]);
      scrollTopError();
      return;
    }

    const categoryForApi = (cat) =>
      cat && cat !== 'select' ? TextTransformDatabaseEnum(cat) : cat;

    const updatePayload = {
      kitchen_name: KitchenName,
      product_sku: sku,
    };

    if ('product_name' in changedFields) updatePayload.product_name = productName;
    if ('recipe_with' in changedFields) updatePayload.recipe_with = recipeWith;
    if ('product_description' in changedFields) updatePayload.product_description = description;
    if ('product_category' in changedFields)
      updatePayload.product_category = categoryForApi(productCategory);
    if ('special_marking' in changedFields) updatePayload.special_marking = specialMarking;
    if ('serving_temperature' in changedFields)
      updatePayload.serving_temperature = servingTemperature;
    if ('cuisine' in changedFields) updatePayload.cuisine = cuisineType;
    if ('size' in changedFields) updatePayload.size = size;
    if ('serving_size' in changedFields) updatePayload.serving_size = servingSize;
    if ('meal_type' in changedFields) updatePayload.meal_type = mealType;
    if ('available_quantity' in changedFields)
      updatePayload.available_quantity = parseInt(quantity, 10);
    if (hasServingDaysChanged())
      updatePayload.serving_days = selectedDays.map((d) => d.toLowerCase());
    if (productType === 'non_meal_item' && hasToppingsChanged())
      updatePayload.extra_toppings = cleanExtraToppings;

    if (imageFile && KitchenName) {
      const productKey = getFileCacheKey(imageFile);
      const cached = productImageCacheRef.current;
      if (cached.cacheKey === productKey && cached.secureUrl) {
        updatePayload.image_url = cached.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('fileName', `${KitchenName}_${product?.product_sku}`);
        formData.append('kitchen_name', KitchenName);
        const response = await uploadImageCloudinary(formData);

        if (response?.hasError || response?.data?.success === false) {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during product image upload. Please try again later or contact support.',
              param: 'image_url',
            },
          ]);
          scrollTopError();
          return;
        }

        if (response?.data?.success && response?.data?.secureUrl) {
          const secureUrl = response.data.secureUrl;
          productImageCacheRef.current = { cacheKey: productKey, secureUrl };
          updatePayload.image_url = secureUrl;
        } else {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during product image upload. Please try again later or contact support.',
              param: 'image_url',
            },
          ]);
          scrollTopError();
          return;
        }
      }
    }

    if (hasIngredientImageFromProduct && ingredientImageFile && KitchenName) {
      const ingredientKey = getFileCacheKey(ingredientImageFile);
      const cachedIng = ingredientImageCacheRef.current;
      if (cachedIng.cacheKey === ingredientKey && cachedIng.secureUrl) {
        updatePayload.ingredients_image = cachedIng.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', ingredientImageFile);
        formData.append('fileName', `${KitchenName}_ingredientImage_${product?.product_sku}`);
        formData.append('kitchen_name', KitchenName);
        const responseTwo = await uploadImageCloudinary(formData);

        if (responseTwo?.hasError || responseTwo?.data?.success === false) {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during ingredients image upload. Please try again later or contact support.',
              param: 'ingredients_image',
            },
          ]);
          scrollTopError();
          return;
        }

        if (responseTwo?.data?.success && responseTwo?.data?.secureUrl) {
          const ingredientSecureUrl = responseTwo.data.secureUrl;
          ingredientImageCacheRef.current = {
            cacheKey: ingredientKey,
            secureUrl: ingredientSecureUrl,
          };
          updatePayload.ingredients_image = ingredientSecureUrl;
        } else {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during ingredients image upload. Please try again later or contact support.',
              param: 'ingredients_image',
            },
          ]);
          scrollTopError();
          return;
        }
      }
    }

    const safePayload = omitRegionalForbiddenProductKeys(updatePayload);

    setIsLoading(true);
    try {
      const res = await regionalUpdateProduct(safePayload);

      if (res?.hasError) {
        setMissingFields([
          {
            errorMessage:
              res.message === API_NETWORK_ERROR
                ? 'Network error. Please try again.'
                : res.message || 'Failed to update product.',
            param: 'update_product',
          },
        ]);
        scrollTopError();
        return;
      }

      const body = res?.data ?? {};

      if (body.success === true) {
        if (typeof onUpdate === 'function') {
          onUpdate(body.product ?? safePayload);
        }
        if (typeof onClose === 'function') {
          onClose();
        }
        setOpenSnackBar(true);
      } else {
        setMissingFields(mapRegionalProductUpdateErrorsToForm(body));
        scrollTopError();
      }
    } catch (err) {
      console.error('Update product error', err);
      setMissingFields([
        {
          errorMessage: 'Unexpected error while updating product. Please try again.',
          param: 'update_product',
        },
      ]);
      scrollTopError();
    } finally {
      setIsLoading(false);
    }
  };

  const changedCount = Object.keys(changedFields).length;

  const pendingFields = [];
  Object.keys(changedFields).forEach((key) => {
    if (fieldLabelMap[key]) pendingFields.push(fieldLabelMap[key]);
  });
  if (hasServingDaysChanged()) pendingFields.push(fieldLabelMap.serving_days);
  if (productType === 'non_meal_item' && hasToppingsChanged())
    pendingFields.push(fieldLabelMap.extra_toppings);
  if (imageFile) pendingFields.push(fieldLabelMap.image_url);
  if (hasIngredientImageFromProduct && ingredientImageFile)
    pendingFields.push(fieldLabelMap.ingredients_image);
  const fieldsToBeUpdatedUnique = [...new Set(pendingFields)];

  return {
    formTopRef,
    productType,
    productName,
    setProductName,
    recipeWith,
    setRecipeWith,
    description,
    setDescription,
    productCategory,
    setProductCategory,
    specialMarking,
    setSpecialMarking,
    servingTemperature,
    setServingTemperature,
    cuisineType,
    setCuisineType,
    size,
    setSize,
    servingSize,
    setServingSize,
    mealType,
    setMealType,
    price,
    setPrice,
    quantity,
    setQuantity,
    selectedDays,
    setSelectedDays,
    offers,
    ingredients,
    allergicIngredients,
    extraToppings,
    setExtraToppings,
    imageFile,
    setImageFile,
    previewUrl,
    setPreviewUrl,
    ingredientImageFile,
    setIngredientImageFile,
    ingredientPreviewUrl,
    setIngredientPreviewUrl,
    errors,
    missingFields,
    openSnackBar,
    setOpenSnackBar,
    isLoading,
    editConfirmed,
    setEditConfirmed,
    editingFields,
    changedFields,
    changedCount,
    categoryList,
    fieldsToBeUpdatedUnique,
    startEdit,
    cancelField,
    trackField,
    onChangeHandler,
    handleUpdateProduct,
    hasIngredientImageFromProduct,
  };
};
